<template>
  <div class="flex flex-col min-h-0 min-w-0 w-full h-full">
    <div
      class="border-b border-mid-neutral-50 h-14 flex items-center justify-between px-6 bg-white z-10 shrink-0"
    >
      <div class="flex items-center gap-2 text-dark-neutral-500">
        <span class="material-symbols-outlined">flag</span>
        <h2 class="font-semibold">Markets</h2>
      </div>
    </div>
    <div class="w-full px-6 py-4">
      <div class="bg-gray-50 w-full rounded-t-2xl">
        <div class="flex items-center justify-between px-5 py-4 w-full">
          <div class="flex gap-3 h-12 items-center">
            <img
              src="https://flagcdn.com/eu.svg"
              alt="EU"
              class="w-8 h-8 rounded-full object-cover"
            />
            <span class="font-semibold text-gray-900 text-base"
              >EAA, Turkey and Northern Ireland</span
            >
          </div>
          <Switch v-model="isEAAEnabled">
            <template #icon>
              <span
                class="material-symbols-outlined !text-lg"
                :class="{
                  'text-green-500': isEAAEnabled,
                  'text-mid-neutral-200': !isEAAEnabled,
                }"
              >
                mode_off_on
              </span>
            </template>
          </Switch>
        </div>
      </div>
      <div class="bg-white w-full rounded-b-2xl">
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 p-5"
        >
          <button
            v-for="country in countries"
            :key="country.code"
            type="button"
            @click="toggleCountry(country.code)"
            :class="[
              'box-border text-left content-stretch cursor-pointer flex items-start justify-start p-0 relative w-full group',
              selectedCountries.includes(country.code) ? 'z-10' : '',
            ]"
            data-name="Checkbox options"
          >
            <div
              :class="[
                'basis-0 box-border content-stretch flex gap-2.5 grow  items-center justify-start min-h-px min-w-px px-5 py-4 relative rounded-lg shrink-0 bg-white border transition-colors',
                selectedCountries.includes(country.code)
                  ? 'border-primary-500 text-primary-500'
                  : 'border-mid-neutral-50 text-slate-800',
                'hover:border-primary-500 focus:border-primary-500',
              ]"
              data-name="checkbox options"
            >
              <div
                aria-hidden="true"
                :class="[
                  'absolute border border-solid inset-0 pointer-events-none rounded-lg',
                  selectedCountries.includes(country.code)
                    ? 'border-primary-500'
                    : 'border-mid-neutral-50',
                ]"
              />
              <div
                class="basis-0 box-border content-stretch flex gap-2.5 grow items-center justify-start min-h-px min-w-px p-0 relative shrink-0"
                data-name="Option"
              >
                <div class="relative shrink-0 w-8 h-8" data-name="Country">
                  <img
                    :src="`https://flagcdn.com/w80/${country.code}.png`"
                    :alt="country.name"
                    class="block max-w-none w-8 h-8 rounded-full object-cover object-center"
                  />
                </div>
                <div
                  :class="[
                    'basis-0 font-medium grow leading-none min-h-px min-w-px not-italic overflow-ellipsis overflow-hidden relative shrink-0 text-sm text-nowrap',
                    selectedCountries.includes(country.code)
                      ? 'text-primary-500'
                      : 'text-slate-800',
                  ]"
                >
                  <p class="block leading-[1.4] whitespace-pre">
                    {{ country.name }}
                  </p>
                </div>
              </div>
              <div
                class="bg-white relative rounded shrink-0 w-4 h-4 flex items-center justify-center ml-auto overflow-clip"
              >
                <span
                  :class="[
                    'w-4 h-4 rounded border flex items-center justify-center transition-colors',
                    selectedCountries.includes(country.code)
                      ? 'bg-primary-500 border-primary-500'
                      : 'bg-white border-mid-neutral-100',
                    'group-hover:border-primary-500 group-focus:border-primary-500',
                  ]"
                >
                  <span
                    v-if="selectedCountries.includes(country.code)"
                    class="material-symbols-outlined text-white text-lg leading-none"
                  >
                    check_small
                  </span>
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Switch } from "ui-components";
import { ref } from "vue";

const countries = [
  { name: "Austria", code: "at" },
  { name: "Belgium", code: "be" },
  { name: "Bulgaria", code: "bg" },
  { name: "Croatia", code: "hr" },
  { name: "Cyprus", code: "cy" },
  { name: "Czech Republic", code: "cz" },
  { name: "Denmark", code: "dk" },
  { name: "Estonia", code: "ee" },
  { name: "Finland", code: "fi" },
  { name: "France", code: "fr" },
  { name: "Germany", code: "de" },
  { name: "Greece", code: "gr" },
  { name: "Hungary", code: "hu" },
  { name: "Iceland", code: "is" },
  { name: "Ireland", code: "ie" },
  { name: "Italy", code: "it" },
  { name: "Latvia", code: "lv" },
  { name: "Liechtenstein", code: "li" },
  { name: "Lithuania", code: "lt" },
  { name: "Luxembourg", code: "lu" },
  { name: "Malta", code: "mt" },
  { name: "Netherlands", code: "nl" },
  { name: "Norway", code: "no" },
  { name: "Poland", code: "pl" },
  { name: "Portugal", code: "pt" },
  { name: "Romania", code: "ro" },
  { name: "Slovakia", code: "sk" },
  { name: "Slovenia", code: "si" },
  { name: "Spain", code: "es" },
  { name: "Sweden", code: "se" },
  { name: "Turkey", code: "tr" },
  { name: "Northern Ireland", code: "gb-nir" },
];

const isEAAEnabled = ref(false);

const selectedCountries = ref<string[]>([]);
function toggleCountry(code: string) {
  const idx = selectedCountries.value.indexOf(code);
  if (idx === -1) {
    selectedCountries.value.push(code);
  } else {
    selectedCountries.value.splice(idx, 1);
  }
}
</script>
